/**
 * Schedule & shots for the open mission: one shared copy for the planner, Live and Replay.
 *
 * Edits apply locally at once and save on their own shortly after (autosave). A save carries the
 * revision it was based on; if someone else saved first the server refuses it, we show their version
 * and say so. Crew tick shots off through a small endpoint that never needs the whole document.
 */
import { create } from 'zustand';
import { emptySchedulePlan, type Id, type SchedulePlan, type SchedulePlanRecord, type ShotStatus } from '@apos/shared';
import { ApiError, errorMessage, request } from '../lib/api';
import { onPlanChanged } from './mission';

export type PlanSaveState = 'saved' | 'pending' | 'saving' | 'error' | 'conflict';

export interface PlanState {
  missionId: Id | null;
  /** Last version the server confirmed. */
  record: SchedulePlanRecord | null;
  /** What the editor shows (record.plan plus unsaved edits). */
  plan: SchedulePlan;
  loading: boolean;
  loadError: string | null;
  save: PlanSaveState;
  saveError: string | null;
  load(missionId: Id): Promise<void>;
  reload(): Promise<void>;
  /** Apply an edit to a copy of the plan and autosave it. */
  edit(fn: (plan: SchedulePlan) => void): void;
  /** Replace the whole plan (templates). */
  replace(plan: SchedulePlan): void;
  flush(): Promise<void>;
  setShotStatus(shotId: Id, status: ShotStatus): Promise<void>;
}

const SAVE_DELAY_MS = 700;
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;

export const usePlan = create<PlanState>((set, get) => {
  const fetchRecord = (mid: Id) => request<SchedulePlanRecord>('GET', `/missions/${mid}/production-plan`);

  const doSave = async (): Promise<void> => {
    if (inFlight) {
      await inFlight;
      if (get().save !== 'pending') return;
    }
    const { missionId, record, plan } = get();
    if (!missionId || !record) return;
    set({ save: 'saving', saveError: null });
    const sent = plan;
    inFlight = (async () => {
      try {
        const next = await request<SchedulePlanRecord>('PUT', `/missions/${missionId}/production-plan`, { revision: record.revision, plan: sent });
        if (get().missionId !== missionId) return;
        // keep edits made while this save was on the wire
        const changedSince = get().plan !== sent;
        set({ record: next, plan: changedSince ? get().plan : next.plan, save: changedSince ? 'pending' : 'saved' });
        if (changedSince) schedule();
      } catch (e) {
        if (get().missionId !== missionId) return;
        if (e instanceof ApiError && e.status === 409) {
          const latest = await fetchRecord(missionId).catch(() => null);
          if (latest) set({ record: latest, plan: latest.plan });
          set({ save: 'conflict', saveError: 'Someone else changed the plan at the same time. You are now seeing their version — re-do your last change.' });
        } else {
          set({ save: 'error', saveError: errorMessage(e) });
        }
      }
    })();
    try {
      await inFlight;
    } finally {
      inFlight = null;
    }
  };

  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void doSave();
    }, SAVE_DELAY_MS);
  };

  onPlanChanged((mid, revision) => {
    const s = get();
    if (s.missionId !== mid || !s.record || revision <= s.record.revision) return;
    // only pull when nothing local is waiting; a pending save will learn of the change from its 409
    if (s.save === 'saved' || s.save === 'conflict') void get().reload();
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (e) => {
      const st = get().save;
      if (st === 'pending' || st === 'saving' || st === 'error') {
        void get().flush();
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  return {
    missionId: null,
    record: null,
    plan: emptySchedulePlan(),
    loading: false,
    loadError: null,
    save: 'saved',
    saveError: null,

    async load(missionId) {
      if (get().missionId === missionId && get().record) return;
      if (timer) clearTimeout(timer);
      set({ missionId, record: null, plan: emptySchedulePlan(), loading: true, loadError: null, save: 'saved', saveError: null });
      try {
        const record = await fetchRecord(missionId);
        if (get().missionId === missionId) set({ record, plan: record.plan, loading: false });
      } catch (e) {
        if (get().missionId === missionId) set({ loading: false, loadError: errorMessage(e) });
      }
    },

    async reload() {
      const mid = get().missionId;
      if (!mid) return;
      try {
        const record = await fetchRecord(mid);
        if (get().missionId !== mid) return;
        if (get().save === 'pending' || get().save === 'saving') return;
        set({ record, plan: record.plan, loadError: null, save: get().save === 'conflict' ? 'conflict' : 'saved' });
      } catch (e) {
        set({ loadError: errorMessage(e) });
      }
    },

    edit(fn) {
      const next = structuredClone(get().plan);
      fn(next);
      set({ plan: next, save: 'pending', saveError: null });
      schedule();
    },

    replace(plan) {
      set({ plan, save: 'pending', saveError: null });
      schedule();
    },

    async flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (get().save === 'pending' || get().save === 'error') await doSave();
      else if (inFlight) await inFlight;
    },

    async setShotStatus(shotId, status) {
      const mid = get().missionId;
      if (!mid) return;
      // optimistic: the tick shows at once
      const before = get().plan;
      set({ plan: { ...before, shots: before.shots.map((s) => (s.id === shotId ? { ...s, status } : s)) } });
      try {
        const next = await request<SchedulePlanRecord>('POST', `/missions/${mid}/production-plan/shots/${shotId}`, { status });
        if (get().missionId !== mid) return;
        const pending = get().save === 'pending' || get().save === 'saving';
        // a producer mid-edit keeps their draft but adopts the new revision and this one shot
        set({
          record: next,
          plan: pending ? { ...get().plan, shots: get().plan.shots.map((s) => next.plan.shots.find((n) => n.id === s.id && n.id === shotId) ?? s) } : next.plan,
        });
      } catch (e) {
        set({ plan: { ...get().plan, shots: get().plan.shots.map((s) => (s.id === shotId ? before.shots.find((b) => b.id === shotId) ?? s : s)) } });
        throw e;
      }
    },
  };
});
